import { SceneState } from '../../../components/SceneStatemachinePlugin';
import { StarBreathingGuide } from '../display/StarBreathingGuide';
import { StarBreathingTrail } from '../display/StarBreathingTrail';
import { ANGLE_TOLERANCE, HANDS, STAR_BREATHING_VO } from '../config/Config';
import { StarBreathingInput } from '../input/StarBreatingInput';
import { StarBreathingParticle } from '../display/StarBreathingParticle';
import { CURSOR_OVER, DEFAULT_PROMPT_TIMEOUT } from '../../../constants/Constants';

export class StateStarBreathingDraw extends SceneState {
  startState() {
    this.pathDistance = 0;
    this.complete = false;
    const pathConfig = HANDS.human;
    this.path = pathConfig.path.map((p, i) => ({
      ...p,
      distance: i == 0 ? 0 : Phaser.Math.Distance.BetweenPoints(pathConfig.path[i - 1], p),
      angle: i == 0 ? 0 : Phaser.Math.Angle.BetweenPoints(pathConfig.path[i - 1], p),
    }));

    this.hand = this.scene.add.image(910, 780, pathConfig.image.sheet, pathConfig.image.key);
    this.hand.setOrigin(0.5, 1);
    this.pathGuide = new StarBreathingGuide(this.scene, this.hand.x, this.hand.y, this.path);
    this.pathTrail = new StarBreathingTrail(this.scene, this.hand.x, this.hand.y);

    this.pathTrailStar = this.scene.add.image(0, 0, 'star_breathing', 'star_trail');
    this.pathTrailStar.visible = false;

    this.pathTrailStarBurst = new StarBreathingParticle(this.scene, this.hand.x, this.hand.y);
    this.starBreatingInput = new StarBreathingInput(this.scene, this.pathTrailStar);
    this.starBreatingInput.on('move', this.updateUserInputPath, this);
    this.starBreatingInput.on('pointerdown', this.endPrompt, this);
    this.starBreatingInput.on(
      'pointerup',
      () => {
        if (!this.complete) this.startPrompt(true);
      },
      this
    );

    this.userPointRegistered = { ...this.path[0], distance: 0 };
    this.userPathPositionIndex = 0;
    this.userPathStarsCollected = 0;
    this.userPathDistanceFromLastStar = 0;

    this.scene.tweens.add({
      targets: this.hand,
      y: { from: 2000, to: 780 },
      duration: 500,
      onComplete: () => {
        this.scene.game.vo.playChain(STAR_BREATHING_VO.user_start, {
          onComplete: this.startInput,
          onCompleteScope: this,
        });
      },
    });
  }

  startInput() {
    this.handTween = null;
    this.pathTrailStar.visible = true;
    this.pathTrailStar.setPosition(this.hand.x + this.path[0].x, this.hand.y + this.path[0].y);
    this.starBreatingInput.enable();
    this.pathGuide.renderPathGuideToStar(this.userPathPositionIndex);

    this.scene.tweens.add({
      targets: this.pathTrailStar,
      scale: { from: 1, to: 1.2 },
      repeat: 1,
      yoyo: true,
      duration: 150,
    });
    this.scene.game.vo.play(STAR_BREATHING_VO.user_timeout_options[0]);
    this.startPrompt(true);
  }

  startPrompt(trackStop) {
    if (trackStop) {
      this.scene.game.trackPromptStop();
    }
    if (this.promptTimer) {
      this.promptTimer.remove();
      this.promptTimer = null;
    }
    this.promptTimer = this.scene.time.addEvent({
      delay: DEFAULT_PROMPT_TIMEOUT,
      loop: true,
      callback: () => {
        this.scene.game.trackPromptStart();
        this.scene.tweens.add({
          targets: this.pathTrailStar,
          scale: { from: 1, to: 1.2 },
          repeat: 1,
          yoyo: true,
          duration: 150,
        });
        this.scene.game.vo.play(
          STAR_BREATHING_VO.user_timeout_options[
            Math.floor(Math.random() * STAR_BREATHING_VO.user_timeout_options.length)
          ]
        );
      },
    });
  }

  endPrompt() {
    if (this.promptTimer) {
      this.scene.game.trackPromptStop();
      this.promptTimer.remove();
      this.promptTimer = null;
    }
  }

  onSceneUpdate(time, delta) {
    if (this.pathTrail) this.pathTrail.update(delta);
    if (this.pathTrailStarBurst) this.pathTrailStarBurst.update(delta);
  }

  // INPUT PATH UPDATE
  updateUserInputPath({ distance, angle }) {
    if (distance <= 0) return;
    this.scene.game.trackPromptStop();
    this.pathTrailStar.visible = true;
    let distanceRemaining = distance + this.userPointRegistered.distance;
    let completedPoint = false;

    let userPathDistance = 0;
    for (let point, i = this.userPathPositionIndex; i < this.path.length; i++) {
      point = this.path[i];
      const angleDifference = Phaser.Math.RadToDeg(Math.abs(point.angle - angle));
      if (i == 0 || angleDifference < ANGLE_TOLERANCE) {
        let drawDistance = Math.min(point.distance, distanceRemaining);
        if (drawDistance >= point.distance) {
          // we have reached the next point, tick it off and move on
          this.userPathPositionIndex++;
          this.pathTrailStar.setPosition(this.hand.x + point.x, this.hand.y + point.y);
          completedPoint = point;
          drawDistance = point.distance;
          if (i > 0) this.pathTrail.addPoints(this.userPointRegistered, point);
          this.userPointRegistered = { ...point, distance: 0 };
        } else if (i > 0) {
          // we havn't quite reached the next point, cache the distance and carry on next time
          const x = this.path[i - 1].x + drawDistance * Math.cos(point.angle);
          const y = this.path[i - 1].y + drawDistance * Math.sin(point.angle);
          this.pathTrail.addPoints(this.userPointRegistered, { x, y });
          this.pathTrailStar.setPosition(this.hand.x + x, this.hand.y + y);
          this.userPointRegistered = { x, y, distance: drawDistance };
        }

        distanceRemaining -= drawDistance;
        userPathDistance += drawDistance;
        if (completedPoint?.star || distanceRemaining <= 0) break;
      } else break;
    }

    console.log(userPathDistance);

    if (this.userPathDistanceFromLastStar >= 0) {
      this.userPathDistanceFromLastStar += userPathDistance;
      if (userPathDistance > 0 && this.userPathDistanceFromLastStar > 200) {
        this.userPathDistanceFromLastStar = -1;
        this.playStarTargetVO(this.userPathStarsCollected);
      }
    }

    if (completedPoint) {
      if (this.userPathPositionIndex >= this.path.length) {
        this.handlePathComplete();
      } else if (completedPoint.star) {
        this.handleStarReached(completedPoint);
      } else this.pathGuide.renderPathGuideToStar(this.userPathPositionIndex);
    }
  }

  handleStarReached(star) {
    this.userPathStarsCollected++;
    this.userPathDistanceFromLastStar = 0;
    if (this.userPathStarsCollected > 1) {
      this.scene.game.soundController.playSFX('sfx', 'star');
      this.starBreatingInput.disable();
      this.pathGuide.clear();
      this.pathTrailStar.setPosition(this.hand.x + star.x, this.hand.y + star.y);
      this.pathTrailStarBurst.addStars(10, star.x, star.y);
      this.scene.tweens.add({
        targets: this.pathTrailStar,
        scale: { from: 1, to: 1.5 },
        duration: 250,
        yoyo: true,
      });
      this.pathTrailStar.time = 0;
      this.scene.tweens.add({
        targets: this.pathTrailStar,
        time: { from: 0, to: 1 },
        duration: 750,
        onComplete: () => this.resumeAfterStarReached(),
      });
    } else {
      this.resumeAfterStarReached();
    }
  }

  resumeAfterStarReached() {
    // this.playStarTargetVO(this.userPathStarsCollected);
    this.pathGuide.renderPathGuideToStar(this.userPathPositionIndex);
    this.starBreatingInput.enable();
    if (this.scene.input.activePointer.isDown) {
      this.starBreatingInput.handlePointerDown(this.scene.input.activePointer);
    }
  }

  playStarTargetVO(targetIndex) {
    const vo = targetIndex % 2 == 1 ? STAR_BREATHING_VO.breathe_in : STAR_BREATHING_VO.breathe_out;
    this.scene.game.vo.playChain(vo);
  }

  handlePathComplete() {
    this.endPrompt();
    this.scene.game.trackPromptStop();
    this.pathTrailStarBurst.addStars(10, this.pathTrailStar.x, this.pathTrailStar.y);
    this.complete = true;
    this.pathGuide.clear();
    this.starBreatingInput.disable();
    this.scene.tweens.add({
      targets: this.hand,
      y: 2000,
      delay: 500,
      duration: 500,
      onComplete: () => this.emit('state_complete'),
    });
  }

  endState() {
    this.endPrompt();
    this.scene.game.trackPromptStop();
    this.scene.game.vo.stop();
    this.starBreatingInput.destroy();
    this.starBreatingInput = null;
    this.removeSceneObject('handImage');
    this.removeSceneObject('pathGuide');
    this.removeSceneObject('pathTrail');
    this.removeSceneObject('pathTrailStar');
    this.removeSceneObject('pathTrailStarBurst');
    this.removeSceneObject('particles');
  }

  removeSceneObject(id) {
    if (this[id]) {
      this.scene.tweens.killTweensOf(this[id]);
      this[id].destroy();
      this[id] = null;
    }
  }
}
